/**
 * @prettier
 * @flow
 */
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { navigate } from '@reach/router';
import React from 'react';
import { useForm } from 'react-hook-form';
import { paymentLinkApi } from 'src/api/paymentLinkApi';
import { BackButton } from 'src/components/buttons/BackButton';
import { Form } from 'src/components/form/Form';
import { FormCheckbox } from 'src/components/form/FormCheckbox';
import { FormContactDetailsField } from 'src/components/form/FormContactDetailsField';
import { FormCurrencyAmountField } from 'src/components/form/FormCurrencyAmountField';
import { FormIntegerField } from 'src/components/form/FormIntegerField';
import { FormMsiField } from 'src/components/form/FormMsiField';
import { FormShippingDetailsField } from 'src/components/form/FormShippingDetailsField';
import { FormSubmitButton } from 'src/components/form/FormSubmitButton';
import { FormTextField } from 'src/components/form/FormTextField';
import type { EmailAddress, PhoneNumber } from 'src/types/Id';
import { alertKnownErrorOrSomethingWentWrong } from 'src/utils/alert/alertKnownErrorOrSomethingWentWrong';
import { useSelector } from 'src/utils/react/useSelector';
import { trim } from 'src/utils/string/trim';
import { translate } from 'src/utils/translate/translate';

export function CreatePaymentLinkPage() {
    const classes = useStyles();

    const form = useForm<FormType>();

    const accountId = useSelector(state => state.app.accountId);

    const askForShippingAddress = form.watch('askForShippingAddress', false);

    const onSubmit = async (form: FormType) => {
        const response = await paymentLinkApi.createPaymentLink({
            accountId,
            customerName: trim(form.customerName),
            reason: trim(form.reason),
            total: (form.totalAmount: any),
            msi: parseInt(form.msi || '0'),
            expiresInDays: parseInt(form.expiresInDays || EXPIRES_IN_DAYS_DEFAULT_VALUE),
            email: trim(form.contact.email),
            phoneNumber: trim(form.contact.phoneNumber),
            askForShippingAddress: !!form.askForShippingAddress,
            shippingAddress: form.askForShippingAddress
                ? {
                      addressLine1: trim(form.shippingAddress.addressLine1),
                      addressLine2: trim(form.shippingAddress.addressLine2),
                      zipCode: trim(form.shippingAddress.zipCode),
                      city: trim(form.shippingAddress.city),
                      state: trim(form.shippingAddress.state),
                  }
                : undefined,
        });
        if (!response.ok) {
            alertKnownErrorOrSomethingWentWrong(response);
            return;
        }
        navigate(`../`);
    };

    return (
        <Paper className={classes.paper}>
            <div className={classes.backButtonContainer}>
                <BackButton />
            </div>
            <Typography variant='h4' align='center' className={classes.header}>
                {translate('Create Payment Link')}
            </Typography>
            {/*<Typography variant="subtitle1" align="center" className={classes.header}>*/}
            {/*    {translate('Hello @customer, you received a payment request from @company', { customer: paymentLink?.customerName, company: 'Let’s Eat' })}*/}
            {/*</Typography>*/}
            <Form form={form} onSubmit={onSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormTextField name='customerName' label={translate('Customer Name')} rules={{ required: true, minLength: 4, maxLength: 100 }} />
                    </Grid>
                    {/*<Grid item xs={12} sm={6}>*/}
                    {/*    <FormTextField*/}
                    {/*        name="email"*/}
                    {/*        label={translate('Email')}*/}
                    {/*        rules={{ required: true, email: true, maxLength: 100 }}*/}
                    {/*    />*/}
                    {/*</Grid>*/}
                    <Grid item xs={12} sm={12}>
                        <FormTextField name='reason' label={translate('Payment Reason')} rules={{ required: true, maxLength: 100 }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormCurrencyAmountField name='totalAmount' label={translate('Total Amount')} rules={{ required: true, min: 1, max: 1000000 }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormIntegerField
                            name='expiresInDays'
                            label={translate('Expires in')}
                            InputProps={{ endAdornment: <InputAdornment position='end'>{translate('days')}</InputAdornment> }}
                            rules={{ min: 1, max: 90 }}
                            placeholder={EXPIRES_IN_DAYS_DEFAULT_VALUE}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={3}>
                                <FormMsiField name='msi' label={translate('Months without Interest')} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <FormContactDetailsField name='contact' />
                    </Grid>
                    <Grid item xs={12}>
                        <FormCheckbox name='askForShippingAddress' label={translate('Ask for Shipping Address')} defaultValue={false} />
                    </Grid>
                    {askForShippingAddress && (
                        <Grid item xs={12}>
                            <FormShippingDetailsField name='shippingAddress' />
                        </Grid>
                    )}
                </Grid>
                <Grid container spacing={2} direction='column' justifyContent='space-around' alignItems='center' className={classes.buttonContainer}>
                    {/*<Grid item>*/}
                    {/*    <Typography variant="subtitle2" align="center">*/}
                    {/*        {`${translate('Total Amount')} ${formatAsCurrency('paymentLink?.total')}`}*/}
                    {/*    </Typography>*/}
                    {/*</Grid>*/}
                    <Grid item>
                        <FormSubmitButton text={translate('Create')} />
                    </Grid>
                </Grid>
            </Form>
        </Paper>
    );
}

const EXPIRES_IN_DAYS_DEFAULT_VALUE = '2';

const useStyles = makeStyles(theme => ({
    header: {
        paddingBottom: theme.spacing(4),
    },
    backButtonContainer: {
        position: 'absolute',
    },
    paper: {
        padding: theme.spacing(2),
        [theme.breakpoints.up(800 + theme.spacing(3) * 2)]: {
            padding: theme.spacing(3),
        },
    },
    buttonContainer: {
        paddingTop: theme.spacing(4),
    },
}));

type Props = {
    accountId?: string,
};

type FormType = {|
    customerName: string,
    reason: string,
    totalAmount: string,
    msi: string,
    expiresInDays: string,
    contact: {|
        email: EmailAddress,
        phoneNumber: PhoneNumber,
    |},
    askForShippingAddress: boolean,
    shippingAddress: {|
        addressLine1: string,
        addressLine2: string,
        zipCode: string,
        city: string,
        state: string,
    |},
|};
